import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, User, Send, CheckCircle2, UserPlus } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import axios from 'axios';

const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, login, member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const referralCode = searchParams.get('ref') || '';
  
  // Redirect if already logged in
  useEffect(() => {
    if (member) {
      navigate('/dashboard');
    }
  }, [member, navigate]);

  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const memberData = await register(
        registerForm.name,
        registerForm.email,
        registerForm.password,
        referralCode
      );
      toast.success(`Welcome to TGA! Your member number is ${memberData.member_number}`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/members/forgot-password`, {
        email: forgotEmail
      });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reset email';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg flex flex-col">
      {/* Back button */}
      <div className="p-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="text-muted-foreground hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>

      {/* Auth Form */}
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={TGA_LOGO} 
              alt="TGA Pro Tour" 
              className="h-24 object-contain"
              data-testid="auth-logo"
            />
          </div>

          <Card className="bg-card border-white/10">
            <Tabs defaultValue={referralCode ? "register" : "login"} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted">
                <TabsTrigger 
                  value="login"
                  className="data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="login-tab"
                >
                  Login
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="register-tab"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
                  <CardDescription>Login to access your TGA account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-primary hover:bg-primary/90 rounded-pill btn-hover-scale"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setForgotSent(false); setForgotEmail(''); }}
                        className="text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                </CardContent>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Join the TGA</CardTitle>
                  <CardDescription>Create your account and get a unique member number</CardDescription>
                </CardHeader>
                <CardContent>
                  {referralCode && (
                    <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-secondary/10 border border-secondary/30" data-testid="referral-banner">
                      <UserPlus className="w-4 h-4 text-secondary shrink-0" />
                      <p className="text-sm text-secondary">
                        You were invited by a TGA member! You'll both earn a badge.
                      </p>
                    </div>
                  )}
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="John Doe"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="your@email.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="register-password-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-confirm"
                          type="password"
                          placeholder="••••••••"
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                          className="pl-10 bg-muted border-border focus:border-primary"
                          required
                          data-testid="register-confirm-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-pill btn-hover-scale"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>

          {/* Forgot Password Overlay */}
          {showForgotPassword && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6" data-testid="forgot-password-modal">
              <Card className="bg-card border-white/10 w-full max-w-md animate-fade-in">
                {forgotSent ? (
                  <CardContent className="p-8 text-center">
                    <CheckCircle2 className="w-14 h-14 text-secondary mx-auto mb-4" />
                    <h3 className="text-xl font-heading text-white mb-2">Check Your Email</h3>
                    <p className="text-muted-foreground mb-6 text-sm">
                      If an account exists for <span className="text-white">{forgotEmail}</span>, we sent a password reset link. Check your inbox (and spam folder).
                    </p>
                    <Button
                      onClick={() => setShowForgotPassword(false)}
                      className="bg-primary hover:bg-primary/90 rounded-pill px-8"
                      data-testid="forgot-done-btn"
                    >
                      Back to Login
                    </Button>
                  </CardContent>
                ) : (
                  <>
                    <CardHeader>
                      <CardTitle className="font-heading text-2xl">Forgot Password</CardTitle>
                      <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="forgot-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="forgot-email"
                              type="email"
                              placeholder="your@email.com"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              className="pl-10 bg-muted border-border focus:border-primary"
                              required
                              data-testid="forgot-email-input"
                            />
                          </div>
                        </div>
                        <Button
                          type="submit"
                          className="w-full bg-primary hover:bg-primary/90 rounded-pill btn-hover-scale"
                          disabled={loading}
                          data-testid="forgot-submit-btn"
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {loading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                        <div className="text-center">
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(false)}
                            className="text-sm text-muted-foreground hover:text-white transition-colors"
                            data-testid="forgot-cancel-btn"
                          >
                            Back to Login
                          </button>
                        </div>
                      </form>
                    </CardContent>
                  </>
                )}
              </Card>
            </div>
          )}

          {/* Sponsor Banner - Large display */}
          <SponsorBanner variant="hero" className="mt-8" />
        </div>
      </div>
    </div>
  );
};
